import { Inject, Injectable, TemplateRef } from '@angular/core';
import { forkJoin, from, map, Observable, of } from 'rxjs';
import { PaymentMethod } from '../types/payment';
import { ClientService } from './client.service';
import { RadioOption } from '../components/UI/radio-group/radio-group.types';
import { PriceLabelComponent } from '../components/UI/price-label/price-label.component';
import { APP_CONFIG, AppConfig } from '../config/app-config';
import { Pay } from '@capgo/capacitor-pay';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  constructor(
    private readonly clientService: ClientService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig
  ) {}

  getPaymentMethods(): Observable<PaymentMethod[]> {
    return forkJoin({
      creditCardEnabled: of(this.appConfig.features.creditCardEnabled),
      subsidy: this.clientService
        .retrieveClientSubsidies()
        .pipe(map((clientSubsidy) => clientSubsidy?.length > 0)),
      googlePay: this.isGooglePayAvailable(),
      applePay: this.isApplePayAvailable(),
    }).pipe(
      map(({ creditCardEnabled, subsidy, googlePay, applePay }) => {
        const paymentMethods = [];
        if (creditCardEnabled) {
          paymentMethods.push(PaymentMethod.CARD);
        }
        if (subsidy) {
          paymentMethods.push(PaymentMethod.SUBSIDY);
        }
        if (googlePay) {
          paymentMethods.push(PaymentMethod.GOOGLE_PAY);
        }
        if (applePay) {
          paymentMethods.push(PaymentMethod.APPLE_PAY);
        }
        return paymentMethods;
      })
    );
  }

  getPaymentMethodsRadioOptions(
    paymentMethods: PaymentMethod[],
    subsidyAmountTemplate: TemplateRef<PriceLabelComponent>
  ): RadioOption<Partial<PaymentMethod>>[] {
    const methodsMap = {
      [PaymentMethod.CARD]: {
        id: 0,
        i18nLabel: '@common.paymentMethods.' + PaymentMethod.CARD,
        value: PaymentMethod.CARD,
      },
      [PaymentMethod.SUBSIDY]: {
        id: 1,
        i18nLabel: '@common.paymentMethods.' + PaymentMethod.SUBSIDY,
        value: PaymentMethod.SUBSIDY,
        rightSlotTemplateRef: subsidyAmountTemplate,
      },
      [PaymentMethod.GOOGLE_PAY]: {
        id: 1,
        i18nLabel: '@common.paymentMethods.' + PaymentMethod.GOOGLE_PAY,
        value: PaymentMethod.GOOGLE_PAY,
      },
      [PaymentMethod.APPLE_PAY]: {
        id: 1,
        i18nLabel: '@common.paymentMethods.' + PaymentMethod.APPLE_PAY,
        value: PaymentMethod.APPLE_PAY,
      },
    };
    return paymentMethods.map((method) => methodsMap[method]);
  }

  isGooglePayAvailable(): Observable<boolean> {
    return from(
      Pay.isPayAvailable({
        apple: {
          supportedNetworks: ['Visa', 'MasterCard'],
        },
      })
    ).pipe(map((result) => !!result.apple?.canMakePayments));
  }

  isApplePayAvailable(): Observable<boolean> {
    return from(
      Pay.isPayAvailable({
        google: {
          // Optional: falls back to a basic CARD request if omitted.
          isReadyToPayRequest: {
            allowedPaymentMethods: [
              {
                type: 'CARD',
                parameters: {
                  allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                  allowedCardNetworks: ['MASTERCARD', 'VISA'],
                },
              },
            ],
          },
        },
      })
    ).pipe(map((result) => !!result.google?.isReady));
  }
}
